import { MenuItem, TextField } from "@material-ui/core";
import classnames from "classnames";
import _ from "lodash";
import math from "mathjs";
import { CorrectLens, equals } from "ramda";
import * as React from "react";
import TextareaAutosize from "react-autosize-textarea";
import { connect } from "react-redux";
import { getRequestProgress } from "src/requestProgress/selector";
import QuiqButton from "src/shared/QuiqButton";
import selectorsToProps from "src/utils/selectorsToProps";
import { isFlyoutReasonsFeatureEnabled } from "../../configState/selector";
import { FRACTION_DIGITS } from "../../constants";
import {
  isRequesting,
  transitionIfFinished
} from "../../requestProgress/helpers";
import { actions, RequestProgressState } from "../../requestProgress/reducer";
import { formatMoneyValue } from "../../utils/formatters";
import * as numbers from "../../utils/numbers";
import { AccountPresenter } from "../model/presenter/types";
import { deselectCells } from "../model/selectedCells/actions";
import {
  updateAccount,
  UpdateAccountActionType
} from "../model/spread/actions";
import { getLanguage } from "../model/spread/selector";
import { Spread, SpreadFinancialValue } from "../model/spread/types";
import "./ArithmeticFlyout.scss";

const ARITHMETIC_OPERATORS = new RegExp(/(\+|-|\*|\/)/);

interface ArithmeticFlyoutBaseProps {
  account: AccountPresenter;
  lens: CorrectLens<Spread, SpreadFinancialValue>;
  period: number;
  areFlyoutReasonsEnabled: boolean;
  periodValue: number | undefined;
  defaultReason?: string;
  periodString: string;
}
interface ArithmeticFlyoutProps extends ArithmeticFlyoutBaseProps {
  deselectCells: typeof deselectCells;
  updateAccount: UpdateAccountActionType;
  requestProgress: RequestProgressState;
  language?: string | null;
}

interface ArithmeticFlyoutState {
  expression: string;
  value: number | null;
  mathError: boolean;
  editing: boolean;
  reason: string | null;
  reasonMissing: boolean;
  forceClose: boolean;
  numberTooBigError: boolean;
}

class ArithmeticFlyout extends React.Component<
  ArithmeticFlyoutProps,
  ArithmeticFlyoutState
> {
  constructor(props: ArithmeticFlyoutProps) {
    super(props);

    this.state = {
      expression: props.periodValue ? this.formatNumber(props.periodValue) : "",
      value: null,
      mathError: false,
      editing: false,
      reason: null,
      reasonMissing: false,
      forceClose: false,
      numberTooBigError: false
    };
  }

  public componentWillReceiveProps(nextProps: ArithmeticFlyoutProps) {
    this.setState({
      expression: nextProps.periodValue
        ? this.formatNumber(nextProps.periodValue)
        : "",
      value: null
    });
    const isFinishedState = transitionIfFinished(actions.updateAccount);
    if (isFinishedState && isFinishedState.finished) {
      this.setState({
        expression: nextProps.periodValue
          ? this.formatNumber(nextProps.periodValue)
          : "",
        value: null,
        mathError: false,
        editing: false,
        reason: null,
        reasonMissing: false
      });
      this.props.deselectCells();
    }
  }

  public handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const expressionWithoutComma = String(event.target.value).replace(/,/g, "");
    this.setState({
      expression: expressionWithoutComma,
      value: null,
      mathError: false,
      editing: true,
      reason: null,
      reasonMissing: false
    });
  };

  public handleChangeReason = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    this.setState({
      reason: event.target.value,
      reasonMissing: false
    });
  };

  public handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleSubmit();
    }
  };

  public handleApply = () => {
    const result = this.parseExpression();

    if (math.isNumeric(result)) {
      this.setState({ value: result, mathError: false });
    } else {
      this.setState({ mathError: true });
    }
  };

  public handleSubmit = () => {
    const result = this.parseExpression();

    if (!math.isNumeric(result)) {
      this.setState({ mathError: true });
      return;
    }

    if (this.props.areFlyoutReasonsEnabled && !this.state.reason) {
      this.setState({ reasonMissing: true });
      return;
    }

    if (_.split(result.toFixed(0), "").length > 16) {
      this.setState({ numberTooBigError: true });
      return;
    }

    this.save(result);
  };

  public stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  public render() {
    const { account, language } = this.props;
    const isLoading = isRequesting(actions.updateAccount);
    const name =
      (language &&
        account.translatedNames &&
        account.translatedNames[language]) ||
      account.name;

    return (
      !this.state.forceClose && (
        <div className="Flyout" onMouseDown={this.stopPropagation}>
          <div className="Flyout__content">
            <div className="Flyout__header Flyout__header--blue">
              {name.toUpperCase()}
            </div>
            <div className="Flyout__data">
              <div className="ArithmeticFlyout__text">
                Arithmetic expressions (+, -, *, /) can be used to perform
                calculations.
              </div>
              <div className="ArithmeticFlyout__textarea-container">
                <TextareaAutosize
                  className={classnames("ArithmeticFlyout__textarea", {
                    "ArithmeticFlyout__textarea--red":
                      this.state.mathError || this.state.numberTooBigError
                  })}
                  value={this.state.expression}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  autoFocus={true}
                  disabled={isLoading}
                />
                {this.state.mathError && (
                  <small className="ArithmeticFlyout__error-text">
                    Invalid Arithmetic expression.
                  </small>
                )}

                {this.state.numberTooBigError && (
                  <small className="ArithmeticFlyout__error-text">
                    The number is too large.
                  </small>
                )}
              </div>
              {ARITHMETIC_OPERATORS.test(this.state.expression) && (
                <React.Fragment>
                  <div className="ArithmeticFlyout__container">
                    <QuiqButton
                      className="ArithmeticFlyout__actions__apply"
                      onClick={this.handleApply}
                      color="white"
                      isDisabled={isLoading}
                    >
                      Apply
                    </QuiqButton>
                  </div>
                  <div className="Flyout__label--bold">
                    Total:{" "}
                    {math.isNumeric(this.state.value)
                      ? formatMoneyValue(this.state.value)
                      : "--"}
                  </div>
                </React.Fragment>
              )}

              {this.props.areFlyoutReasonsEnabled
                ? this.renderFlyoutReasonsEnabled()
                : this.renderFlyoutReasonsNotEnabled()}
            </div>
          </div>
        </div>
      )
    );
  }

  public save = (value: number) => {
    const { account, periodValue, period } = this.props;
    const newValueNumber = value ? value : null;
    const parsedPeriodValue = periodValue === undefined ? null : periodValue;

    if (!equals(newValueNumber, parsedPeriodValue)) {
      this.props.updateAccount({
        financialValueLens: this.props.lens,
        newValue: newValueNumber,
        accountId: account.id,
        period,
        spreadSheetType: account.spreadSheetType,
        reason: this.state.reason || this.props.defaultReason || "",
        periodString: this.props.periodString
      });
    } else {
      this.setState({ forceClose: true });
    }
  };

  private parseExpression = () => {
    try {
      if (!numbers.isAlphabetical(this.state.expression)) {
        const result = math.eval(this.state.expression) as number;
        if (result === undefined) {
          return NaN;
        } else if (result && numbers.isValidDoubleValue(result)) {
          return result;
        }
      }
      return;
    } catch (error) {
      // expression failed
      return;
    }
  };

  private formatNumber = (x: number) =>
    x.toFixed(!Number.isInteger(x) ? FRACTION_DIGITS : 0);

  private renderFlyoutReasonsNotEnabled() {
    if (!this.state.editing) {
      return;
    }

    const isLoading = isRequesting(actions.updateAccount);

    return (
      <React.Fragment>
        {ARITHMETIC_OPERATORS.test(this.state.expression) && (
          <hr
            key="divider"
            className="Flyout__divider Flyout__divider--space"
          />
        )}
        <div className="ArithmeticFlyout__container">
          <QuiqButton
            className="ArithmeticFlyout__actions__submit"
            color="blue"
            onClick={this.handleSubmit}
            isLoading={isLoading}
          >
            Submit
          </QuiqButton>
        </div>
      </React.Fragment>
    );
  }

  private renderFlyoutReasonsEnabled() {
    if (!this.state.editing) {
      return;
    }

    const isLoading = isRequesting(actions.updateAccount);

    return [
      <hr key="divider" className="Flyout__divider Flyout__divider--space" />,

      <TextField
        key="reasons"
        id="outlined-select-reason"
        className="ArithmeticFlyout__reasons"
        select={true}
        disabled={isLoading}
        label="Reason for edit"
        value={this.state.reason === null ? "" : this.state.reason}
        onChange={this.handleChangeReason}
        SelectProps={{
          MenuProps: {
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left"
            },
            MenuListProps: {
              classes: {
                root: "ArithmeticFlyout__reasons__list"
              }
            }
          },
          classes: {
            root: "ArithmeticFlyout__reasons__select"
          }
        }}
        InputLabelProps={{
          classes: {
            root: "ArithmeticFlyout__reasons__label",
            focused: "ArithmeticFlyout__reasons--focused"
          }
        }}
        InputProps={{
          classes: {
            input: "ArithmeticFlyout__reasons__input",
            focused: "ArithmeticFlyout__reasons--focused",
            notchedOutline: this.state.reasonMissing
              ? "ArithmeticFlyout__reasons--error"
              : "ArithmeticFlyout__reasons__outline"
          }
        }}
        FormHelperTextProps={{
          classes: {
            root: this.state.reasonMissing
              ? "ArithmeticFlyout__reasons__helper-text--error"
              : "ArithmeticFlyout__reasons--helperText"
          }
        }}
        helperText="Required"
        margin="normal"
        variant="outlined"
      >
        <MenuItem
          key="misread"
          value="Value read incorrectly"
          className="ArithmeticFlyout__reasons__list-item"
        >
          Value read incorrectly
        </MenuItem>
        <MenuItem
          key="mismapped"
          value="Value mapped incorrectly"
          className="ArithmeticFlyout__reasons__list-item"
        >
          Value mapped incorrectly
        </MenuItem>
      </TextField>,

      <div key="submit" className="ArithmeticFlyout__container">
        <QuiqButton
          className="ArithmeticFlyout__actions__submit"
          color="blue"
          onClick={this.handleSubmit}
          isLoading={isLoading}
        >
          Submit
        </QuiqButton>
      </div>
    ];
  }
}

export default connect(
  selectorsToProps({
    areFlyoutReasonsEnabled: isFlyoutReasonsFeatureEnabled,
    requestProgress: getRequestProgress,
    language: getLanguage
  }),
  {
    deselectCells,
    updateAccount: updateAccount.action
  }
)(ArithmeticFlyout);

export { ArithmeticFlyout };



// WEBPACK FOOTER //
// ./src/spread/chartOfAccounts/ArithmeticFlyout.tsx